import { useEffect } from 'react';
import { reportMissingTransactionAccount } from '@/utils/reportMissingTransactionAccount';
import { useBankCatalog } from '@/hooks/useBankCatalog';
import { formatBankAccountLabel } from '@/utils/bankAccountLabel';
import type { Transaction } from '@/types/payments';

/**
 * The owning bank account of a transaction on one line (FIN-802, Draft B): the ledger account in
 * the foreground, then the bank and masked number muted. Truncates rather than wrapping, with the
 * whole label in the tooltip. A transaction with no account shows `—`, never a guessed account,
 * and is reported to Sentry.
 */
export function TransactionAccountLabel({
  transaction,
  surface,
}: {
  transaction: Pick<Transaction, 'account' | 'accountId'>;
  surface: 'table' | 'dialog';
}) {
  const { getBankDisplayName } = useBankCatalog();
  const { account, accountId } = transaction;

  useEffect(() => {
    if (!account) reportMissingTransactionAccount(accountId, surface);
  }, [account, accountId, surface]);

  if (!account) {
    return (
      <span className="text-muted-foreground" data-testid="transaction-account-label">
        —
      </span>
    );
  }

  const label = formatBankAccountLabel({
    bankDisplayName: getBankDisplayName(account.bic, account.bankName),
    maskedNumber: account.maskedNumber,
    chartAccountNumber: account.chartAccountNumber,
  });

  return (
    <span
      className="block truncate whitespace-nowrap"
      title={label.full}
      data-testid="transaction-account-label"
    >
      {label.ledger && (
        <>
          <span className="font-mono tabular-nums">{label.ledger}</span>
          <span className="text-muted-foreground"> · </span>
        </>
      )}
      <span className="text-muted-foreground">{label.tail}</span>
    </span>
  );
}
